/**
 * dual-time.js — G5 · cross-timezone "Open now" helper.
 *
 * When a member in Mumbai (IST) browses a Tokyo restaurant (JST),
 * we want to surface BOTH times so they understand what "open until
 * 11pm" actually means relative to their day.
 *
 * Usage:
 *   import { dualTimeLine } from "@/lib/dual-time";
 *   const line = dualTimeLine("Asia/Tokyo");
 *   //  → "18:42 in Tokyo · 15:12 for you"  (when member is in Mumbai)
 *   //  → null  (when member's own zone matches the venue's zone)
 *
 * Returns null if either side fails (defensive · never crash a drawer).
 *
 * The venue's time_zone comes from Google Place Details
 * (`places.timeZone` in the Places v1 field mask). We already fetch
 * it across /table, /stay, /fit, /exclusive, /enjoy. This module is
 * the renderer.
 */

function _shortZoneName(iana) {
  if (!iana || typeof iana !== "string") return null;
  // "Asia/Tokyo" → "Tokyo" · best-effort short label for the dual line
  const parts = iana.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
}

function _memberZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_e) {
    return null;
  }
}

function _formatInZone(date, iana) {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: iana,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  } catch (_e) {
    return null;
  }
}

/**
 * Returns a single sentence describing the current time in the
 * venue's timezone AND in the member's timezone, or null when the
 * two match (no need to disambiguate) or when the venue's timezone
 * is unknown / invalid.
 *
 * @param {string|null|undefined} venueZone — IANA timezone, e.g. "Asia/Tokyo"
 * @param {Date} [now] — defaults to new Date() (overrideable for tests)
 * @returns {string|null}
 */
export function dualTimeLine(venueZone, now = new Date()) {
  if (!venueZone) return null;
  const memberZone = _memberZone();
  if (!memberZone || memberZone === venueZone) return null;
  const venueLabel  = _shortZoneName(venueZone);
  const venueTime   = _formatInZone(now, venueZone);
  const memberTime  = _formatInZone(now, memberZone);
  if (!venueTime || !memberTime) return null;
  return `${venueTime} in ${venueLabel} · ${memberTime} for you`;
}

/**
 * Lower-level helper used by some pillars that want to compose their
 * own line. Returns { venueTime, memberTime, venueLabel, sameZone }.
 */
export function dualTimeParts(venueZone, now = new Date()) {
  const memberZone = _memberZone();
  if (!venueZone || !memberZone) {
    return { venueTime: null, memberTime: null, venueLabel: null, sameZone: false };
  }
  const sameZone = memberZone === venueZone;
  return {
    venueZone,
    venueLabel:  _shortZoneName(venueZone),
    venueTime:   _formatInZone(now, venueZone),
    memberZone,
    memberTime:  _formatInZone(now, memberZone),
    sameZone,
  };
}
